/**
 * Enhanced WebSocket Manager for QuantrolixAI Dashboard
 * Handles authentication, reconnection, and real-time data streaming
 *
 * Security: JWT is never sent as a URL query parameter. Authentication
 * is performed by sending an 'auth' message after the connection opens,
 * keeping tokens out of browser history, server access logs, and proxy logs.
 */

import logger from '../utils/logger';

const DEFAULT_CHANNELS = [
  'dashboard_stats',
  'revenue_updates',
  'product_updates',
  'alert_notifications',
  'agent_updates',
  'scraper_updates',
];

export class WebSocketManager {
  constructor(baseUrl) {
    this.baseUrl = baseUrl.replace(/^http/, 'ws');
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.listeners = new Map();
    this.isAuthenticated = false;
    this.heartbeatInterval = null;
    this.connectionState = 'disconnected'; // disconnected, connecting, connected, error
    this.subscriptions = new Set(DEFAULT_CHANNELS);
    this._pendingAuthToken = null;
  }

  /**
   * Connect to WebSocket with optional authentication.
   *
   * The token is NOT appended to the URL. Instead it is sent as an
   * 'auth' message once the connection is open, avoiding token
   * exposure in logs, browser history, and intermediary proxies.
   */
  async connect(authToken = null) {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.connectionState === 'connecting')) {
      logger.debug('WebSocket already connected');
      return;
    }

    this.connectionState = 'connecting';
    this.notifyStateChange('connecting');

    // Store the token so it can be sent after the connection opens
    if (authToken) {
      this._pendingAuthToken = authToken;
    }

    try {
      // Connect WITHOUT the token in the URL
      this.ws = new WebSocket(this.baseUrl);

      this.ws.onopen = () => {
        logger.info('WebSocket connected');
        this.connectionState = 'connected';
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000; // Reset delay
        this.notifyStateChange('connected');

        // Authenticate via message instead of URL parameter
        if (this._pendingAuthToken) {
          this.authenticate(this._pendingAuthToken);
        }

        // Start heartbeat
        this.startHeartbeat();

        // Re-subscribe to previous subscriptions (will be repeated after auth if required)
        this.resubscribeAll();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          logger.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        logger.error('WebSocket error:', error);
        this.connectionState = 'error';
        this.notifyStateChange('error', error);
      };

      this.ws.onclose = (event) => {
        logger.warn('WebSocket disconnected:', event.code, event.reason);
        this.connectionState = 'disconnected';
        this.stopHeartbeat();
        this.isAuthenticated = false;
        this.notifyStateChange('disconnected', { code: event.code, reason: event.reason });

        // Attempt reconnection if not a normal closure
        if (event.code !== 1000 && event.code !== 1001) {
          this.scheduleReconnect();
        }
      };

    } catch (error) {
      logger.error('Failed to create WebSocket connection:', error);
      this.connectionState = 'error';
      this.notifyStateChange('error', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Send authentication message over the open WebSocket connection.
   * This keeps the JWT out of the URL entirely.
   */
  authenticate(token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({
        type: 'auth',
        token: token
      });
    }
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel) {
    this.subscriptions.add(channel);

    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isAuthenticated) {
      this.send({
        type: 'subscribe',
        channel: channel
      });
    }
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel) {
    this.subscriptions.delete(channel);

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({
        type: 'unsubscribe',
        channel: channel
      });
    }
  }

  /**
   * Resubscribe to all channels after reconnection
   */
  resubscribeAll() {
    for (const channel of this.subscriptions) {
      this.send({
        type: 'subscribe',
        channel: channel
      });
    }
  }

  /**
   * Send message through WebSocket
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
      this.ws.send(messageStr);
    } else {
      logger.warn('WebSocket is not connected, cannot send message:', message);
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.send({
          type: 'ping',
          timestamp: new Date().toISOString()
        });
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(data) {
    const { type, channel, payload, status } = data;

    // Handle authentication response
    if (type === 'auth') {
      this.isAuthenticated = status === 'authenticated';
      logger.debug('Authentication status:', this.isAuthenticated);

      if (this.isAuthenticated) {
        // Ensure default channels are tracked
        DEFAULT_CHANNELS.forEach((channel) => this.subscriptions.add(channel));
        // Send subscriptions for all tracked channels now that auth succeeded
        this.resubscribeAll();
      }
      return;
    }

    // Handle subscription confirmations
    if (type === 'subscription') {
      logger.debug(`Subscribed to channel: ${data.channel}`);
      return;
    }

    // Handle pong responses
    if (type === 'pong') {
      // Connection is alive
      return;
    }

    // Handle data messages
    if (type === 'data' && channel) {
      this.notifyListeners(channel, payload);
      return;
    }

    // Handle other message types
    this.notifyListeners(type, data);
  }

  /**
   * Notify listeners for a specific channel/type
   */
  notifyListeners(channel, data) {
    const listeners = this.listeners.get(channel);
    if (!listeners || listeners.size === 0) {
      return;
    }

    [...listeners].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        logger.error(`Error in WebSocket listener for ${channel}:`, error);
      }
    });
  }

  /**
   * Notify connection state change
   */
  notifyStateChange(state, details = null) {
    this.notifyListeners('connectionState', { state, details });
  }

  /**
   * Add event listener
   */
  on(channel, callback) {
    if (!this.listeners.has(channel)) {
      this.listeners.set(channel, new Set());
    }
    const listeners = this.listeners.get(channel);
    listeners.add(callback);

    // Return unsubscribe function
    return () => {
      const set = this.listeners.get(channel);
      if (!set) return;
      set.delete(callback);
      if (set.size === 0) {
        this.listeners.delete(channel);
      }
    };
  }

  /**
   * Remove all listeners for a channel
   */
  off(channel) {
    this.listeners.delete(channel);
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('Max reconnection attempts reached, giving up');
      this.connectionState = 'error';
      this.notifyStateChange('error', { reason: 'Max reconnection attempts reached' });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    logger.info(
      `Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`,
    );

    setTimeout(() => {
      if (this.connectionState !== 'connected') {
        const token = this._pendingAuthToken
          || (typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null);
        this.connect(token);
      }
    }, delay);
  }

  /**
   * Force reconnection
   */
  reconnect() {
    if (this.ws) {
      this.ws.close();
    }

    this.reconnectAttempts = 0;
    const token = this._pendingAuthToken
      || (typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null);
    this.connect(token);
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    this.stopHeartbeat();
    this.subscriptions.clear();
    this._pendingAuthToken = null;

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.listeners.clear();
    this.connectionState = 'disconnected';
    this.isAuthenticated = false;
  }

  /**
   * Get current connection state
   */
  getState() {
    return {
      connectionState: this.connectionState,
      isAuthenticated: this.isAuthenticated,
      reconnectAttempts: this.reconnectAttempts,
      subscriptions: Array.from(this.subscriptions)
    };
  }
}

/**
 * Create a singleton WebSocket manager instance
 */
let wsManager = null;

export function getWebSocketManager(baseUrl = null) {
  if (!wsManager && baseUrl) {
    wsManager = new WebSocketManager(baseUrl);
  }
  return wsManager;
}

export function createWebSocketManager(baseUrl) {
  return new WebSocketManager(baseUrl);
}
